import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Clock } from 'lucide-react';

// Google redirects back here (registered as an Authorized redirect URI on the
// Google Cloud OAuth client) with ?code=...&state=.... redirect_uri must match
// exactly what was sent in the initial authorize request — see LoginPage.js.
export default function AuthCallback() {
  const { loginWithGoogleCode } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);
  const [state, setState] = useState('processing'); // processing | pending | error

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const returnedState = params.get('state');
    const expectedState = sessionStorage.getItem('google_oauth_state');
    sessionStorage.removeItem('google_oauth_state');

    (async () => {
      if (!code || !returnedState || returnedState !== expectedState) {
        navigate('/login?error=google', { replace: true });
        return;
      }
      try {
        const redirectUri = `${window.location.origin}/auth/google/callback`;
        await loginWithGoogleCode(code, redirectUri);
        navigate('/', { replace: true });
      } catch (err) {
        const detail = err?.response?.data?.detail;
        if (detail === 'pending_approval') {
          setState('pending');
        } else {
          navigate('/login?error=google', { replace: true });
        }
      }
    })();
  }, [loginWithGoogleCode, navigate]);

  if (state === 'pending') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8] p-6" data-testid="auth-pending">
        <div className="max-w-sm w-full text-center glass-card rounded-2xl p-8 animate-fade-in-up">
          <div className="w-14 h-14 rounded-2xl bg-[#C67D5B]/10 flex items-center justify-center mx-auto mb-5">
            <Clock size={26} strokeWidth={1.5} className="text-[#C67D5B]" />
          </div>
          <h2 className="text-xl font-['Manrope'] font-bold text-[#2C2C2E] mb-2">Awaiting approval</h2>
          <p className="text-sm text-[#6B7280] leading-relaxed">
            Thanks for signing in with Google. Your request has been sent to the studio owner. You'll be able to access the workspace once it's approved.
          </p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="mt-6 h-11 w-full bg-[#3A8C8C] text-white rounded-full font-medium text-sm hover:bg-[#2E7A7A] transition-colors"
            data-testid="pending-back-btn"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAF8] gap-4" data-testid="auth-callback">
      <div className="w-8 h-8 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" />
      <p className="text-sm text-[#6B7280]">Signing you in…</p>
    </div>
  );
}
